import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import permissionsService from '../services/permissions.service';
import { usePermissions } from '../contexts/PermissionContext';
import { activeMarkets, allMarkets, useMarketConditions, type MarketNoteImage } from '../services/marketConditionsStore';
import { marketConditionDot, marketConditionNote, marketConditionTone, marketStatusLabel } from '../utils/marketConditionStatus';
import MarketConditionsImageViewer from './MarketConditionsImageViewer';
import ModalHeader from './ui/ModalHeader';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Market to bring into view when a user clicks its update toast. */
  focusMarket?: string | null;
  onManage: () => void;
}

const pillTone = {
  green: 'border-emerald-400 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 dark:border-emerald-500 dark:bg-emerald-950 dark:text-emerald-100 dark:hover:bg-emerald-900',
  yellow: 'border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-500 dark:bg-amber-950 dark:text-amber-100 dark:hover:bg-amber-900',
  red: 'border-rose-400 bg-rose-50 text-rose-900 hover:bg-rose-100 dark:border-rose-500 dark:bg-rose-950 dark:text-rose-100 dark:hover:bg-rose-900',
} as const;

/** Header count of written notes; the modal always shows every market. */
export default function MarketConditionsPill({ open, onOpenChange, focusMarket, onManage }: Props) {
  const { notes, loaded, failed } = useMarketConditions();
  const { permissions } = usePermissions();
  const [canEdit, setCanEdit] = useState(false);
  const noteCount = activeMarkets(notes).length;
  const listedMarkets = allMarkets(notes);
  const modalRef = useRef<HTMLDivElement>(null);
  const [viewer, setViewer] = useState<{ market: string; images: MarketNoteImage[]; index: number } | null>(null);
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago' }).format(new Date());
  const displayToday = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', month: '2-digit', day: '2-digit', year: 'numeric' }).format(new Date());
  const todayStatuses = Object.values(notes).filter(note => note.conditionDay === today).map(note => note.conditionStatus);
  const pillStatus = todayStatuses.includes('red') ? 'red' : todayStatuses.includes('yellow') ? 'yellow' : 'green';

  // Show Edit exactly when the save would be allowed: the same can_write grant
  // on the market-conditions PUT that the server enforces. Deliberately NOT
  // gated on the Scheduler page -- the drawer is reachable from any page, and a
  // page check only hid the button from users who were in fact allowed to save.
  useEffect(() => {
    if (!open) { setCanEdit(false); return; }
    let active = true;
    setCanEdit(false);
    void permissionsService.getUserApiPermissions()
      .then(result => {
        if (active) setCanEdit(result.endpoints?.some(endpoint =>
          endpoint.method.toUpperCase() === 'PUT'
          && endpoint.path === '/api/v1/market-conditions/{market}'
          && endpoint.can_write,
        ) ?? false);
      })
      .catch(() => { if (active) setCanEdit(false); });
    return () => { active = false; };
  }, [open, permissions?.user_id]);

  useEffect(() => {
    if (open) return;
    setViewer(null);
  }, [open]);

  const close = () => { setViewer(null); onOpenChange(false); };

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { if (viewer) setViewer(null); else onOpenChange(false); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onOpenChange, viewer]);

  useEffect(() => {
    if (!open || !focusMarket) return;
    modalRef.current?.querySelector<HTMLElement>(`[data-market="${focusMarket}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, focusMarket]);

  // No pill at all when the read never succeeded -- a user without the grant,
  // or an outage. Rendering the default green "Normal Operations" for every
  // market would be stating the opposite of "unknown". A failure that still
  // has an earlier good snapshot keeps showing it until the poll recovers.
  if (!loaded || (failed && !Object.keys(notes).length)) return null;

  return <>
    <button
      type="button"
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={`Market conditions: ${pillStatus} status, ${noteCount} ${noteCount === 1 ? 'market reporting conditions' : 'markets reporting conditions'}`}
      onClick={() => onOpenChange(true)}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium ${pillTone[pillStatus]}`}
    >
      <span aria-hidden="true" className={`h-2 w-2 rounded-full ${marketConditionDot[pillStatus]}`} />
      Market Conditions · {noteCount}
    </button>
    {open && createPortal(
      <div role="dialog" aria-modal="true" aria-labelledby="market-conditions-title" className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-6">
        <div className="absolute inset-0 bg-scrim/60" onClick={close} />
        <div ref={modalRef} className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-cmp-modal shadow-xl">
          <ModalHeader title={`Market Conditions - ${displayToday}`} titleId="market-conditions-title" onClose={close} />
          <div className="overflow-y-auto p-4 sm:p-5">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {listedMarkets.map(market => {
                const note = notes[market];
                const status = note?.conditionDay === today ? note.conditionStatus ?? 'green' : 'green';
                const images = note?.images ?? [];
                const daysOut = note?.conditionDay === today ? note.daysOut : null;
                // Column layout so the attribution can sit on the bottom edge
                // (mt-auto) however short the note above it is.
                return <section key={market} data-market={market} className={`flex h-full flex-col rounded-lg border p-3 ${marketConditionTone[status]} ${focusMarket === market ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold">{market}{daysOut != null && <span className="ml-2 whitespace-nowrap text-xs font-medium opacity-80">· {daysOut} days out</span>}</h3>
                    <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${marketConditionDot[status]}`} role="img" aria-label={`${marketStatusLabel(status)} status`} title={marketStatusLabel(status)} />
                  </div>
                  <p className="mt-2 whitespace-pre-wrap break-words text-sm">{marketConditionNote(note?.text)}</p>
                  {images.length > 0 && <button type="button" className="mt-2 rounded px-1 text-left text-xs font-medium hover:bg-black/10 focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label={`View ${market} images (${images.length})`} onClick={() => setViewer({ market, images, index: 0 })}>📷 {images.length}</button>}
                  {note?.conditionDay === today && note.author && <p className="mt-auto pt-3 text-[10px] leading-tight opacity-70">Updated by {note.author.split('@')[0]} · {new Date(note.updatedAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}</p>}
                </section>;
              })}
            </div>
          </div>
          {canEdit && <div className="flex justify-end border-t border-theme-border-primary px-4 py-3"><button type="button" className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700" onClick={() => { onOpenChange(false); onManage(); }}>Edit market conditions</button></div>}
        </div>
        {viewer && <MarketConditionsImageViewer inPlace market={viewer.market} images={viewer.images}
          index={viewer.index} onIndexChange={index => setViewer(current => current && { ...current, index })}
          onClose={() => setViewer(null)} />}
      </div>,
      document.body,
    )}
  </>;
}
